import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Map, TileLayer, Marker, Popup } from 'react-leaflet';
import AsyncSelect from 'react-select/async';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Set maximum bounds to restrict panning to a specific region (e.g., Philippines)
const philippinesBounds = [
  [4.5, 116.0], // Southwest coordinates
  [21.0, 127.0], // Northeast coordinates
];

const searchOptions = {
  countrycodes: 'ph', // Restrict to Philippines
  bounded: 1,
  viewbox: '116.0,4.5,127.0,21.0', // Bounding box for Philippines
};

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Create red default icon
const redIcon = new L.Icon({
  iconUrl:
    'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const MAX_ZOOM = 22;

const LeafletMapComponent = ({
  pins,
  width,
  height,
  center,
  zoom,
  scrollWheelZoom,
  dragging,
  zoomControl,
  doubleClickZoom,
  closePopupOnClick,
  zoomSnap,
  zoomDelta,
  trackResize,
  touchZoom,
  bounceAtZoomLimits,
  maxZoom,
  minZoom,
  className,
  style,
  tileLayerUrl,
  tileLayerAttribution,
  onMapClick,
  onMapLoad,
  customMarkerIcon,
  showSearch,
  viewOnly,
  isExpanded,

  ...mapProps
}) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const mapRef = useRef(null);
  const highlightLayerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const containerStyle = {
    width,
    height,
    ...style,
  };

  const mapStyle = {
    height: showSearch ? 'calc(100% - 60px)' : '100%',
    width: '100%',
    overflow: 'hidden',
  };

  const searchLocations = async (inputValue) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          inputValue
        )}&countrycodes=ph&limit=10&polygon_geojson=1&addressdetails=1`
      );
      const data = await response.json();

      return data.map((item) => ({
        value: item,
        label: item.display_name,
        lat: parseFloat(item.lat),
        lon: parseFloat(item.lon),
        boundingbox: item.boundingbox,
        geojson: item.geojson,
      }));
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  const loadOptions = useCallback((inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      return Promise.resolve([]);
    }

    return new Promise((resolve) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(async () => {
        const results = await searchLocations(inputValue);
        resolve(results);
      }, 500);
    });
  }, []);

  const handleLocationSelect = (option) => {
    setSelectedLocation(option);

    if (mapRef.current) {
      const map = mapRef.current.leafletElement;

      // Remove previous highlight
      if (highlightLayerRef.current) {
        map.removeLayer(highlightLayerRef.current);
        highlightLayerRef.current = null;
      }

      // If option is null (cleared), just return
      if (!option) return;

      // Use GeoJSON if available for more accurate boundaries
      if (option.geojson) {
        highlightLayerRef.current = L.geoJSON(option.geojson, {
          style: {
            color: '#dc3545',
            weight: 4,
            fillOpacity: 0,
            opacity: 1,
          },
        }).addTo(map);

        // Fit map to GeoJSON bounds with maxZoom constraint
        map.fitBounds(highlightLayerRef.current.getBounds(), {
          maxZoom: Math.min(maxZoom, 19),
        });
      } else {
        // Fallback to bounding box
        const bbox = option.boundingbox;
        const bounds = [
          [parseFloat(bbox[0]), parseFloat(bbox[2])],
          [parseFloat(bbox[1]), parseFloat(bbox[3])],
        ];

        highlightLayerRef.current = L.rectangle(bounds, {
          color: '#dc3545',
          weight: 4,
          fillOpacity: 0,
          opacity: 1,
        }).addTo(map);

        map.fitBounds(bounds, { maxZoom: Math.min(maxZoom, 19) });
      }
    }
  };

  const isWithinPhilippinesBounds = (lat, lng) => {
    return (
      lat >= philippinesBounds[0][0] &&
      lat <= philippinesBounds[1][0] &&
      lng >= philippinesBounds[0][1] &&
      lng <= philippinesBounds[1][1]
    );
  };

  const isOnLand = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&countrycodes=ph`
      );
      const data = await response.json();
      return data.address && data.address.country_code === 'ph';
    } catch (error) {
      console.error('Land validation error:', error);
      return false;
    }
  };

  const handleMapClick = async (e) => {
    if (!viewOnly && onMapClick) {
      const { lat, lng } = e.latlng;
      if (isWithinPhilippinesBounds(lat, lng)) {
        const landCheck = await isOnLand(lat, lng);
        if (landCheck) {
          onMapClick(e);
        }
      }
    }
  };

  // Handle control key for scroll wheel zoom
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) setIsCtrlPressed(true);
    };
    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) setIsCtrlPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Update map scroll wheel zoom based on control key state
  useEffect(() => {
    if (mapRef.current && scrollWheelZoom) {
      const map = mapRef.current.leafletElement;
      if (isCtrlPressed) {
        map.scrollWheelZoom.enable();
      } else {
        map.scrollWheelZoom.disable();
      }
    }
  }, [isCtrlPressed, scrollWheelZoom]);

  // Invalidate map size when expanded state changes
  useEffect(() => {
    if (mapRef.current) {
      const timeouts = [];

      if (isExpanded) {
        // Multiple invalidations to ensure proper sizing
        timeouts.push(
          setTimeout(() => mapRef.current.leafletElement.invalidateSize(), 50)
        );
        timeouts.push(
          setTimeout(() => mapRef.current.leafletElement.invalidateSize(), 150)
        );
        timeouts.push(
          setTimeout(() => mapRef.current.leafletElement.invalidateSize(), 300)
        );
      }

      return () => timeouts.forEach(clearTimeout);
    }
    return undefined;
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (highlightLayerRef.current && mapRef.current) {
        const map = mapRef.current.leafletElement;
        map.removeLayer(highlightLayerRef.current);
      }
    };
  }, []);

  return (
    <div style={containerStyle} className={className}>
      {showSearch && (
        <div className="mb-3">
          <AsyncSelect
            value={selectedLocation}
            onChange={handleLocationSelect}
            loadOptions={loadOptions}
            placeholder="Search location in Philippines..."
            noOptionsMessage={({ inputValue }) =>
              inputValue.length < 3
                ? 'Type at least 3 characters to search'
                : 'No locations found'
            }
            loadingMessage={() => 'Searching locations...'}
            isClearable
            cacheOptions
            defaultOptions={false}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: '38px',
                border: '1px solid #ced4da',
                '&:hover': {
                  border: '1px solid #ced4da',
                },
              }),
              menu: (base) => ({
                ...base,
                zIndex: 9999,
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999,
              }),
            }}
            menuPortalTarget={document.body}
          />
        </div>
      )}
      <div style={mapStyle}>
        <Map
          ref={mapRef}
          maxBounds={philippinesBounds}
          maxBoundsViscosity={1.0}
          searchOptions={searchOptions}
          center={center}
          zoom={zoom}
          scrollWheelZoom={false}
          dragging={dragging}
          zoomControl={zoomControl}
          doubleClickZoom={doubleClickZoom}
          closePopupOnClick={closePopupOnClick}
          zoomSnap={zoomSnap}
          zoomDelta={zoomDelta}
          trackResize={trackResize}
          touchZoom={touchZoom}
          bounceAtZoomLimits={bounceAtZoomLimits}
          maxZoom={maxZoom}
          minZoom={minZoom}
          style={{ height: '100%', width: '100%' }}
          onReady={onMapLoad}
          onclick={handleMapClick}
          {...mapProps}
        >
          <TileLayer url={tileLayerUrl} attribution={tileLayerAttribution} />

          {pins.map((pin, index) => {
            const popupContent =
              pin.popup || `Lat: ${pin.lat}, Lng: ${pin.lng}`;
            const iconToUse = customMarkerIcon || pin.icon || redIcon;
            return (
              <Marker
                key={pin.id || index}
                position={[pin.lat, pin.lng]}
                {...(iconToUse && { icon: iconToUse })}
              >
                <Popup>
                  {typeof popupContent === 'string' ? (
                    <div>{popupContent}</div>
                  ) : (
                    popupContent
                  )}
                </Popup>
              </Marker>
            );
          })}
        </Map>
      </div>
    </div>
  );
};

LeafletMapComponent.propTypes = {
  pins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      popup: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      icon: PropTypes.instanceOf(Object),
    })
  ),
  width: PropTypes.string,
  height: PropTypes.string,
  center: PropTypes.arrayOf(PropTypes.number),
  zoom: PropTypes.number,
  scrollWheelZoom: PropTypes.bool,
  dragging: PropTypes.bool,
  zoomControl: PropTypes.bool,
  doubleClickZoom: PropTypes.bool,
  closePopupOnClick: PropTypes.bool,
  zoomSnap: PropTypes.number,
  zoomDelta: PropTypes.number,
  trackResize: PropTypes.bool,
  touchZoom: PropTypes.bool,
  bounceAtZoomLimits: PropTypes.bool,
  maxZoom: PropTypes.number,
  minZoom: PropTypes.number,
  className: PropTypes.string,
  style: PropTypes.shape({}),
  tileLayerUrl: PropTypes.string,
  tileLayerAttribution: PropTypes.string,
  onMapClick: PropTypes.func,
  onMapLoad: PropTypes.func,
  customMarkerIcon: PropTypes.instanceOf(Object),
  showSearch: PropTypes.bool,
  viewOnly: PropTypes.bool,
  isExpanded: PropTypes.bool,
};

LeafletMapComponent.defaultProps = {
  pins: [],
  width: '100%',
  height: '100%',
  center: [13.5995, 121.9842],
  zoom: 13,
  scrollWheelZoom: true,
  dragging: true,
  zoomControl: true,
  doubleClickZoom: true,
  closePopupOnClick: true,
  zoomSnap: 1,
  zoomDelta: 1,
  trackResize: true,
  touchZoom: true,
  bounceAtZoomLimits: true,
  maxZoom: MAX_ZOOM,
  minZoom: 6,
  className: '',
  style: {},
  tileLayerUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  tileLayerAttribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  onMapClick: undefined,
  onMapLoad: undefined,
  customMarkerIcon: undefined,
  showSearch: false,
  viewOnly: false,
  isExpanded: false,
};

export default LeafletMapComponent;
